import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import QuizPage from "./pages/QuizPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import IntakeFormPage from "./pages/IntakeFormPage";

// Owner Pages
import OwnerDashboard from "./pages/owner/Dashboard";
import OwnerDogs from "./pages/owner/Dogs";
import OwnerSeries from "./pages/owner/Series";
import OwnerSeriesDetail from "./pages/owner/SeriesDetail";
import OwnerVideoDetail from "./pages/owner/VideoDetail";
import OwnerFamily from "./pages/owner/Family";
import OwnerCommunity from "./pages/owner/Community";
import OwnerLeaderboard from "./pages/owner/Leaderboard";
import OwnerMessages from "./pages/owner/Messages";
import OwnerJournal from "./pages/owner/Journal";
import OwnerSessions from "./pages/owner/Sessions";
import OwnerSettings from "./pages/owner/Settings";
import PaymentSuccess from "./pages/owner/PaymentSuccess";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminSeries from "./pages/admin/Series";
import AdminSeriesDetail from "./pages/admin/SeriesDetail";
import AdminVideoEdit from "./pages/admin/VideoEdit";
import AdminAnalytics from "./pages/admin/Analytics";
import AdminLoginPage from "./pages/admin/AdminLoginPage";

// Protected Route Components
const OwnerRoute = ({ children }) => {
  const { isOwner, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-gradient-edupaws flex items-center justify-center">
      <div className="animate-float">
        <div className="w-16 h-16 glass-sage rounded-full flex items-center justify-center">
          <span className="text-2xl">🐕</span>
        </div>
      </div>
    </div>;
  }
  
  if (!isOwner) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-gradient-edupaws flex items-center justify-center">
      <div className="animate-float">
        <div className="w-16 h-16 glass-sage rounded-full flex items-center justify-center">
          <span className="text-2xl">🐕</span>
        </div>
      </div>
    </div>;
  }
  
  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/quiz" element={<QuizPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/intake" element={<IntakeFormPage />} />
      
      {/* Owner Routes */}
      <Route path="/owner/dashboard" element={
        <OwnerRoute><OwnerDashboard /></OwnerRoute>
      } />
      <Route path="/owner/dogs" element={
        <OwnerRoute><OwnerDogs /></OwnerRoute>
      } />
      <Route path="/owner/series" element={
        <OwnerRoute><OwnerSeries /></OwnerRoute>
      } />
      <Route path="/owner/series/:seriesId" element={
        <OwnerRoute><OwnerSeriesDetail /></OwnerRoute>
      } />
      <Route path="/owner/video/:videoId" element={
        <OwnerRoute><OwnerVideoDetail /></OwnerRoute>
      } />
      <Route path="/owner/family" element={
        <OwnerRoute><OwnerFamily /></OwnerRoute>
      } />
      <Route path="/owner/community" element={
        <OwnerRoute><OwnerCommunity /></OwnerRoute>
      } />
      <Route path="/owner/leaderboard" element={
        <OwnerRoute><OwnerLeaderboard /></OwnerRoute>
      } />
      <Route path="/owner/messages" element={
        <OwnerRoute><OwnerMessages /></OwnerRoute>
      } />
      <Route path="/owner/journal" element={
        <OwnerRoute><OwnerJournal /></OwnerRoute>
      } />
      <Route path="/owner/sessions" element={
        <OwnerRoute><OwnerSessions /></OwnerRoute>
      } />
      <Route path="/owner/settings" element={
        <OwnerRoute><OwnerSettings /></OwnerRoute>
      } />
      <Route path="/owner/payment-success" element={
        <OwnerRoute><PaymentSuccess /></OwnerRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute><AdminDashboard /></AdminRoute>
      } />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/series" element={
        <AdminRoute><AdminSeries /></AdminRoute>
      } />
      <Route path="/admin/series/:seriesId" element={
        <AdminRoute><AdminSeriesDetail /></AdminRoute>
      } />
      <Route path="/admin/video/:videoId/edit" element={
        <AdminRoute><AdminVideoEdit /></AdminRoute>
      } />
      <Route path="/admin/analytics" element={
        <AdminRoute><AdminAnalytics /></AdminRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
